import React from 'react';
import { getFollowupState } from '../../domain/followupLogic';
import NoMeetingRequest from './NoMeetingRequest';
import BookedMeetingDisplay from './BookedMeetingDisplay';
import KonsultProposalsAccepted from './KonsultProposalsAccepted';
import KonsultProposalsWaiting from './KonsultProposalsWaiting';
import CustomerTimeSlots from './CustomerTimeSlots';

/**
 * Huvudkomponent för "Uppföljning estimat"-sektionen
 * Visar rätt sub-komponent baserat på vilket scenario vi är i
 */
const UppföljningSection = ({
  // Visibility
  currentSentAt,
  expanded,
  onToggle,

  // Data för state-beräkning
  builderProposals, // Kommer döpas om till byggkonsultProposals i parent
  estimatFollowup,
  bookedFollowupSlot,
  customerComments,
  
  // Actions
  onSuggestTime,
  onBookMeeting,
  onShowEstimatCustomerView,
  onApproveForCustomer,
  onBookHomeVisit,
  approveLoading,
  isApproved = false,
  onEditMeeting,
  onCancelMeeting,
  isEditingMeeting = false,
  isCancellingMeeting = false,
  
  // Booking state och callbacks
  bookingSlot,
  setBookingSlot,
  setBookedFollowupSlot,
  setFollowupTeamsUrl,
  setResponseModalData,
  setShowResponseModal,
  setShouldRefreshAfterModal,
  
  // User & context
  user,
  customerId,

  // När hembesök är bokat är uppföljning av estimatet inte längre aktuell
  homeVisitBooked = false,

  // Återkalla skickade tidsförslag
  onCancelProposals,
  onCancelSingleProposal,
  isCancellingProposals = false,
}) => {
  // Visa bara om estimat är skickat
  if (!currentSentAt) {
    return null;
  }

  // Beräkna vilket scenario vi är i
  const { scenario, data } = getFollowupState({
    byggkonsultProposals: builderProposals,
    estimatFollowup,
    bookedFollowupSlot,
    customerComments,
  });

  // Samla booking-callbacks i ett objekt för enklare prop-drilling
  const bookingCallbacks = {
    setBookingSlot,
    setBookedFollowupSlot,
    setFollowupTeamsUrl,
    setResponseModalData,
    setShowResponseModal,
    setShouldRefreshAfterModal,
  };

  // Rendera rätt sub-komponent baserat på scenario
  const renderContent = () => {
    // Hembesök bokat → uppföljning av estimatet är inte längre aktuell.
    // Dölj föreslå/boka-knapparna och visa ett meddelande i stället. En ev.
    // redan bokad uppföljningstid visas fortfarande (läs-vänligt) under.
    if (homeVisitBooked) {
      const bookedSlotRaw = scenario === 'MEETING_BOOKED' ? data?.bookedSlot : bookedFollowupSlot;
      const bookedSlotText = typeof bookedSlotRaw === 'string'
        ? bookedSlotRaw
        : (bookedSlotRaw?.displayText || bookedSlotRaw?.selectedSlot || '');
      return (
        <div>
          <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2.5 text-sm text-blue-800">
            Hembesök har bokats, så uppföljning av estimat är inte längre tillgängligt.
          </div>
          {bookedSlotText && (
            <div className="mt-3 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm text-gray-700">
              <span className="font-medium">Bokad uppföljning:</span>
              <span>{bookedSlotText}</span>
            </div>
          )}
        </div>
      );
    }

    switch (scenario) {
      case 'KONSULT_PROPOSAL_ACCEPTED':
        return (
          <KonsultProposalsAccepted
            {...data}
            bookedFollowupSlot={bookedFollowupSlot}
            bookingSlot={bookingSlot}
            estimatFollowup={estimatFollowup}
            onBookSlot={bookingCallbacks}
            onSuggestTime={onSuggestTime}
            onBookMeeting={onBookMeeting}
            onApproveForCustomer={onApproveForCustomer}
            onBookHomeVisit={onBookHomeVisit}
            approveLoading={approveLoading}
            isApproved={isApproved}
            onShowEstimatCustomerView={onShowEstimatCustomerView}
            userRole={user?.role}
            customerId={customerId}
          />
        );

      case 'KONSULT_PROPOSAL_WAITING':
        return (
          <KonsultProposalsWaiting
            {...data}
            bookedFollowupSlot={bookedFollowupSlot}
            estimatFollowup={estimatFollowup}
            onSuggestTime={onSuggestTime}
            onBookMeeting={onBookMeeting}
            onApproveForCustomer={onApproveForCustomer}
            onBookHomeVisit={onBookHomeVisit}
            approveLoading={approveLoading}
            isApproved={isApproved}
            onCancelProposals={onCancelProposals}
            onCancelSingleProposal={onCancelSingleProposal}
            isCancellingProposals={isCancellingProposals}
          />
        );

      case 'MEETING_BOOKED':
        return (
          <BookedMeetingDisplay
            bookedSlot={data.bookedSlot}
            userRole={user?.role}
            onShowEstimatCustomerView={onShowEstimatCustomerView}
            onEditMeeting={onEditMeeting}
            onCancelMeeting={onCancelMeeting}
            isEditing={isEditingMeeting}
            isCancelling={isCancellingMeeting}
            onApproveForCustomer={onApproveForCustomer}
            onBookHomeVisit={onBookHomeVisit}
            approveLoading={approveLoading}
            isApproved={isApproved}
          />
        );

      case 'CUSTOMER_REQUEST_WITH_TIMES':
        return (
          <CustomerTimeSlots
            note={data.note}
            slots={data.slots}
            bookedFollowupSlot={bookedFollowupSlot}
            bookingSlot={bookingSlot}
            onBookSlot={bookingCallbacks}
            onSuggestTime={onSuggestTime}
            onBookMeeting={onBookMeeting}
            onApproveForCustomer={onApproveForCustomer}
            onBookHomeVisit={onBookHomeVisit}
            approveLoading={approveLoading}
            isApproved={isApproved}
            customerId={customerId}
          />
        );

      case 'CUSTOMER_REQUEST_NO_TIMES':
        return (
          <div>
            {data.note && (
              <div className="text-sm text-gray-700 mb-4">
                <span className="font-medium">Kommentar:</span> {data.note}
              </div>
            )}
            <NoMeetingRequest
              onSuggestTime={onSuggestTime}
              onBookMeeting={onBookMeeting}
              onApproveForCustomer={onApproveForCustomer}
              onBookHomeVisit={onBookHomeVisit}
              approveLoading={approveLoading}
              isApproved={isApproved}
            />
          </div>
        );

      case 'NO_REQUEST':
      default:
        return (
          <NoMeetingRequest
            onSuggestTime={onSuggestTime}
            onBookMeeting={onBookMeeting}
            onApproveForCustomer={onApproveForCustomer}
            onBookHomeVisit={onBookHomeVisit}
            approveLoading={approveLoading}
            isApproved={isApproved}
          />
        );
    }
  };

  return (
    <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200">
      {/* Header (expanderbar) */}
      <div
        className="bg-gray-100 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-leadme-gray-dark">Uppföljning</h2>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-4 sm:p-6">
          {renderContent()}
        </div>
      )}
    </div>
  );
};

export default UppföljningSection;
